import cube from '@cubejs-client/core';
import { useContext, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { AuthContext } from 'libs.react_internal';
import { DataReportsContext } from '../../../contexts/data_reports/data_reports.context';
import { resolveCubeApiUrl } from '../../constants/cube.constants';
export const getCubeApi = ({ entityId, studyId, activeReportsTab, selectedTestEnv, prodOrgs, disablePreAggs, env = 'test', isEphemeral, }) => {
    const _cubeApiUrl = resolveCubeApiUrl({ env, activeReportsTab, isEphemeral });
    const effectiveEnv = env || (activeReportsTab === 0 ? 'test' : 'prod');
    const headers = {
        'Cache-Control': 'must-revalidate',
        'X-Entity-Id': entityId || '',
        'X-Study-Id': studyId || '',
        'X-Env': effectiveEnv,
    };
    if (effectiveEnv === 'test' && selectedTestEnv.value) {
        headers['X-Orgs'] = JSON.stringify([selectedTestEnv.value]);
    }
    else if (effectiveEnv === 'prod' && prodOrgs.length > 0) {
        headers['X-Orgs'] = JSON.stringify(prodOrgs);
    }
    if (disablePreAggs === true) {
        headers['X-Preaggs'] = 'false';
    }
    return cube('', {
        apiUrl: _cubeApiUrl,
        headers,
        credentials: 'include',
    });
};
export const useCubeApi = (options = {}) => {
    const { entityId } = useContext(AuthContext);
    const { studyId: paramStudyId } = useParams();
    const { activeReportsTab, selectedTestEnv, devEnvironments, prodEnvironments, studyId: contextStudyId, } = useContext(DataReportsContext);
    const studyId = contextStudyId || paramStudyId;
    const [searchParams] = useSearchParams();
    const prodOrgs = useMemo(() => prodEnvironments?.map((env) => env.cortexId) ?? [], [prodEnvironments]);
    const env = searchParams.get('env');
    const isEphemeral = useMemo(() => devEnvironments?.some((devEnv) => devEnv.cortexId === selectedTestEnv.value && devEnv.deployment.type === 'ephemeral') ?? false, [devEnvironments, selectedTestEnv.value]);
    const prodOrgsKey = useMemo(() => JSON.stringify(prodOrgs), [prodOrgs]);
    const selectedTestEnvKey = useMemo(() => JSON.stringify(selectedTestEnv), [selectedTestEnv.value, selectedTestEnv.label]);
    const cubeApi = useMemo(() => {
        return getCubeApi({
            entityId: `${entityId}`,
            studyId,
            activeReportsTab,
            selectedTestEnv,
            prodOrgs,
            disablePreAggs: options.disablePreAggs,
            env,
            isEphemeral,
        });
    }, [entityId, studyId, activeReportsTab, selectedTestEnvKey, prodOrgsKey, options.disablePreAggs, env, isEphemeral]);
    // A client that always bypasses pre-aggregations. Used to force a truly fresh read on
    // manual dashboard refresh. It is intentionally kept separate from `cubeApi` so the
    // refresh never mutates the normal client's identity — otherwise reverting refresh state
    // would trigger a second (stale, pre-agg) fetch that clobbers the freshly loaded data.
    const freshCubeApi = useMemo(() => {
        return getCubeApi({
            entityId: `${entityId}`,
            studyId,
            activeReportsTab,
            selectedTestEnv,
            prodOrgs,
            disablePreAggs: true,
            env,
            isEphemeral,
        });
    }, [entityId, studyId, activeReportsTab, selectedTestEnvKey, prodOrgsKey, env, isEphemeral]);
    return { cubeApi, freshCubeApi };
};
